import { markRaw, nextTick } from 'vue';
import { Model } from 'vue-model';
import { config } from '../config';
import { type ChatMessage, MAIN_ROOM } from '../types';
import { Connection } from './Connection';
import { User } from './User';
import { log, WARN } from '../log';
import { alert } from '../utils';
import { getApp } from './App';
import type JitsiConference from 'JitsiConference';
import type JibriSession from 'modules/recording/JibriSession';
import type JitsiLocalTrack from 'modules/RTC/JitsiLocalTrack';
import type JitsiTrack from 'modules/RTC/JitsiTrack';
import type JitsiRemoteTrack from 'modules/RTC/JitsiRemoteTrack';
import type { MediaType } from 'service/RTC/MediaType';
import {
  VIRTUAL_ROOMS, onMainConferenceCreated, shareConference, onVirtualTrackAdded,
  onVirtualTrackRemoved, setSpeakerOrder, dominantSpeakerIn, resubscribe,
} from './virtualRooms';

const localTracks: JitsiLocalTrack[] = [];

type x = {
  on(event: string, callback: (track: JitsiTrack) => void): void;
};

const getLocalTrack = async (types: ('audio' | 'camera' | 'desktop')[]) => {
  const app = getApp();

  if (types.includes('camera') && types.includes('desktop')) {
    types.splice(types.indexOf('camera'), 1);
  }

  // eslint-disable-next-line
  for (const track of [...localTracks]) {
    const type = track.getType();
    const videoType = track.getVideoType();
    const ttype = type === 'video' ? videoType : type;

    const trackAlreadyThere = types.some((t) => {
      if (t !== ttype) return false;
      if (ttype === 'audio' && track.getDeviceId() !== app.micDeviceId) return false;
      if ((ttype === 'camera' || !ttype) && track.getDeviceId() !== app.cameraDeviceId) return false;
      return true;
    });

    if (trackAlreadyThere) {
      types.splice(types.indexOf(ttype as 'audio' | 'camera' | 'desktop'), 1);
    } else {
      await track.dispose();
      localTracks.splice(localTracks.indexOf(track), 1);
    }
  }

  if (types.length) {
    try {
      const ts = types.map((t) => {
        return t === 'camera' ? 'video' : t;
      });

      const t = await JitsiMeetJS.createLocalTracks({
        devices: ts,
        micDeviceId: app.micDeviceId ?? undefined,
        cameraDeviceId: app.cameraDeviceId ?? undefined,
      });

      if (Array.isArray(t)) {
        localTracks.push(...t);

        app.audioNotPermitted = false;
        app.videoNotPermitted = false;
        app.micDeviceId = localTracks.find((t) => t.getType() === 'audio')?.getDeviceId() ?? app.micDeviceId;
        app.cameraDeviceId = localTracks.find((t) => t.getType() === 'video')?.getDeviceId() ?? app.cameraDeviceId;
      } else {
        console.log(t);
      }
    } catch (e) {
      const error = e as Error;

      if (error.name === JitsiMeetJS.errors.track.SCREENSHARING_USER_CANCELED) {
        app.isScreenShareEnabled = false;
      }

      if (error.name === 'NotAllowedError' || error.name === JitsiMeetJS.errors.track.PERMISSION_DENIED) {
        const msg = 'You did not grant permissions for audio or video. They will not be available throughout the session.';

        if (types.includes('audio')) {
          app.isAudioEnabled = false;
          app.audioNotPermitted = true;
        }

        if (types.includes('camera')) {
          app.isVideoEnabled = false;
          app.videoNotPermitted = true;
        }

        const check = [] as MediaType[]; // ('audio'|'video')[]
        if (types.includes('audio')) {
          check.push('video' as MediaType.VIDEO);
        } else if (types.includes('camera')) {
          check.push('audio' as MediaType.AUDIO);
        }

        if (check.length) {
          await Promise.all(
            check.map(async (t) => {
              const granted = await JitsiMeetJS.mediaDevices.isDevicePermissionGranted(t);
              if (t === 'video') {
                app.videoNotPermitted = !granted;
              } else if (t === 'audio') {
                app.audioNotPermitted = !granted;
              }
            }),
          );
          // await JitsiMeetJS.mediaDevices.isDevicePermissionGranted(check).then((granted:boolean) => {
          //   if (check.includes('video')) {
          //     app.videoNotPermitted = !granted
          //   }

          //   if (check.includes('audio')) {
          //     app.audioNotPermitted = !granted
          //   }
          // })
        }

        alert(msg);
      }

      console.log('Couldnt get', types, 'because of:', e, JitsiMeetJS.errors.track.PERMISSION_DENIED);
    }
  }

  // const audioTrack = localTracks.find((t) => t.getType() === 'audio');

  // if (audioTrack && !audioTrack._streamEffect?.isEnabled()) {
  //   await audioTrack.setEffect(MicGainEffect)
  // }

  if (types.includes('desktop')) {
    (localTracks.find((t) => t.getType() === 'video' && t.getVideoType() === 'desktop') as unknown as x)?.on(
      JitsiMeetJS.events.track.LOCAL_TRACK_STOPPED,
      () => {
        app.isScreenShareEnabled = false;
      },
    );
  }

  return localTracks;
};

function convertToJitsiRoomName(base: string, name: string) {
  if (name === MAIN_ROOM) {
    return base.replace(/\s+/g, '_').toLowerCase();
  }

  return (base + '_' + name).replace(/\s+/g, '_').toLowerCase();
}

const confOptions = {
  ...config,
  openBridgeChannel: true,
  startVideoMuted: true,
};

type Stats = {
  avgAudioLevels: Record<string, unknown>;
  bandwidth: { download: number; upload: number };
  bitrate: {
    upload: number;
    download: number;
    audio: { upload: number; download: number };
    video: { upload: number; download: number };
  };
  bridgeCount: number;
  codec: Record<string, unknown>;
  connectionQuality: number;
  framerate: Record<string, unknown>;
  localAvgAudioLevels: number | undefined;
  packetLoss: { total: number; download: number; upload: number };
  resolution: Record<string, unknown>;
};

export class Room extends Model {
  static model = 'Room';
  static primaryKey = 'name';

  name!: string;
  displayName!: string;
  serverName!: string;
  users!: User[];
  conference!: JitsiConference;
  tracks!: (JitsiLocalTrack | JitsiRemoteTrack)[];
  joined!: boolean;
  joining!: boolean;
  rejoining!: boolean;
  messages!: ChatMessage[];
  dominantSpeaker!: string | null;
  connectionId!: string;
  connection!: Connection;
  customWhiteBoardName!: string;
  whiteBoardEnabled!: boolean;
  whiteBoardUrl!: string;
  follow!: boolean;
  currentSlide!: number;
  teachersCurrentSlide!: number;
  slideNames!: string[];
  display!: 'whiteboard' | 'screenshare';
  slideChangeInProgress!: number;
  enabled!: boolean;
  joinPromise!: Promise<void>;
  resolveJoin!: () => void;
  rejectJoin!: (e: Error) => void;
  boardSetLoading!: boolean;
  boardSetDeleting!: boolean;
  sortedUsers!: User[];

  // whiteboardName!: string
  // currentWhiteboardName!: string

  static fields() {
    return {
      name: this.string(''),
      displayName: this.string(''),
      users: this.hasMany(User),
      tracks: this.array<JitsiTrack>([], Object as any),
      joined: this.boolean(false),
      joining: this.boolean(false),
      messages: this.array<ChatMessage>([], Object as any),
      dominantSpeaker: this.string(null),
      whiteBoardEnabled: this.boolean(true),
      customWhiteBoardName: this.string(null),
      whiteBoardUrl: this.computed((conf) => {
        return config.whiteBoardUrl + (conf.customWhiteBoardName ?? conf.serverName);
      }),
      follow: this.boolean(false),
      currentSlide: this.number(0),
      teachersCurrentSlide: this.number(0),
      timerExpirationDate: this.number(0),
      serverName: this.computed((room) => {
        return convertToJitsiRoomName(room.connection.name, room.name);
      }),
      connectionId: this.string(null),
      connection: this.belongsTo(Connection),

      sortedUsers: this.computed((room) => {
        return room.users
          .filter((u) => u.visible || getApp().showInvisibleUsers)
          .sort((u1, u2) => {
            if (u1.role !== u2.role) {
              return u1.role === 'teacher' ? -1 : 1;
            }

            if (u1.hand !== u2.hand) {
              return u1.hand ? -1 : 1;
            }

            if (u1.hand === true && u2.hand === true) {
              return u1.handTime < u2.handTime ? -1 : 1;
            }

            return u1.name.localeCompare(u2.name);
          });
      }),

      slideNames: this.field<string[]>([], Object as any),
      display: this.string('whiteboard'),
      slideChangeInProgress: this.number(0),
      enabled: this.boolean(true),
      boardSetLoading: this.boolean(false),
      boardSetDeleting: this.boolean(false),

      // whiteboardName: this.string(),
      // currentWhiteboardName: this.string()
    };
  }

  init() {
    this.joinPromise = new Promise((resolve, reject) => {
      this.resolveJoin = resolve;
      this.rejectJoin = reject;
    });

    let conference: JitsiConference;
    try {
      console.log('[~conference] Initialising conference', this.name);
      conference = markRaw(this.connection.connection.initJitsiConference(this.serverName, confOptions));
    } catch (e: unknown) {
      console.error('Couldnt init conference', e);
      return this.rejectJoin(e as Error);
    }

    const camelCase = (str: string) => {
      return (
        'on' +
        str
          .toLowerCase()
          .split('_')
          .map((s: string) => s.charAt(0).toUpperCase() + s.slice(1))
          .join('')
      );
    };

    const bindEvents = (eventObj: Record<string, string>, _type: string) => {
      Object.entries(eventObj).forEach(([key, value]) => {
        conference.on(value, (...args: any[]) => {
          // This one is particulary annoying
          // if (key !== 'TRACK_AUDIO_LEVEL_CHANGED') {
          //   console.log('[x][' + type + '] ' + key, ...args)
          // }

          if ((this as any)[camelCase(key)]) {
            (this as any)[camelCase(key)](...args);
          }
        });
      });
    };

    bindEvents(JitsiMeetJS.events.conference, 'conference');
    bindEvents(JitsiMeetJS.events.connectionQuality, 'connectionQuality');


    const isVirtualMain = VIRTUAL_ROOMS && this.name === MAIN_ROOM;

     // Are we using the virtual rooms mode of breakout rooms?
    if (isVirtualMain) {
      onMainConferenceCreated(conference); // receive nothing until our room is known
    } else {
      conference.setReceiverConstraints({
        lastN: 5, // Number of videos requested from the bridge.
        defaultConstraints: { maxHeight: 180 }, // Default resolution requested for all endpoints.
      });
      conference.setReceiverVideoConstraint(180);
    }
    conference.setSenderVideoConstraint(180);

    this.conference = conference;
    if (isVirtualMain) shareConference(conference);

    /*
    conference.setReceiverConstraints({
      lastN: 5, // Number of videos requested from the bridge.
      defaultConstraints: { maxHeight: 180 }, // Default resolution requested for all endpoints.
    });

    conference.setReceiverVideoConstraint(180);
    conference.setSenderVideoConstraint(180);

    this.conference = conference;
    */

  }

  onLocalStatsUpdated(stats: Stats) {
    const user = this.getLocalUser();
    if (user) {
      user.connectionQuality = stats.connectionQuality;
    }
  }

  onRemoteStatsUpdated(id: string, stats: Stats) {
    const user = User.getByJitsiId(id);
    if (user) {
      user.connectionQuality = stats.connectionQuality;
    }
  }

  onTalkWhileMuted() {
    getApp().talkWhileMuted = true;
  }

  onNoisyMic() {
    getApp().noisyMic = true;
  }

  onNoAudioInput() {
    getApp().noAudio = true;
  }

  onRecorderStateChanged(session: JibriSession) {
    const app = getApp();
    // const newState = session.getStatus();
    // This is a hack. jibri might be out of date and therefore getStatus() doesnt return the correct state.
    const newState = (session as any)._statusFromJicofo;

    app.recorderState = newState;
    app.recordingSession = session;
  }

  async onLocalTracks(tracks: JitsiLocalTrack[]) {
    const t = this.conference.getLocalTracks();

    await Promise.all(
      t.map(async (t) => {
        if (tracks.includes(t)) return;

        return this.conference.removeTrack(t);
      }),
    );

    await Promise.all(
      tracks.map(async (track) => {
        if (t.includes(track)) return;

        console.log('[~conference] adding local track', track.getType(), track.getVideoType(), track.getDeviceId());

        return this.conference.addTrack(track);
      }),
    );
  }

  getJitsiIdFromTrack = (track: JitsiRemoteTrack | JitsiLocalTrack) => {
    if (track.isLocal()) {
      return this.getLocalUser().jitsiId;
    }
    return track.getParticipantId();
  };

  onTrackAdded(track: JitsiLocalTrack | JitsiRemoteTrack) {
    // Are we using the virtual rooms mode of breakout rooms?
    if (VIRTUAL_ROOMS) return onVirtualTrackAdded(this, track);
    
    if (track.getType() === 'video') {
      track.addEventListener(JitsiMeetJS.events.track.TRACK_VIDEOTYPE_CHANGED, () => {
        console.warn('[~track] Video type changed', track.getVideoType());
        console.warn('We have to re-add the track to the array');
        this.tracks.splice(this.tracks.indexOf(track), 1);
        nextTick(() => {
          this.tracks.push(markRaw(track));

          // Show or hide whiteboard/screenshare based on available tracks
          this.setDisplay();

          // Show or hide video box
          this.showVideoBox();
        });
      });
    }

    this.tracks.push(markRaw(track));

    // TODO: How often does that happen?
    // Dont show any videobox if we cant identify the user of the track
    if (!User.getByJitsiId((track as JitsiRemoteTrack).getParticipantId())) {
      console.warn('[~track] Couldnt identify user of track', track);
      console.warn('Not showing videobox');
      return;
    }

    // Once a video (camera) track has been added, it is not removed by Jitsi until the user leaves the conference.  Instead, it is muted/unmuted.  This section is listening for mutes/unmutes.
    // Show/hide the the video box based on changes of "muting/unmuting".
    track.addEventListener(JitsiMeetJS.events.track.TRACK_MUTE_CHANGED, () => {
      // Show or hide video box
      this.showVideoBox();
      this.setDisplay();
    });

    // Show or hide whiteboard/screenshare based on available tracks
    this.setDisplay();

    // Show or hide video box
    this.showVideoBox();

    console.log('[~conference] track added', track.getType(), track.getVideoType(), track.getParticipantId());
  }

  onTrackRemoved(track: JitsiLocalTrack | JitsiRemoteTrack) {
    // Are we using the virtual rooms mode of breakout rooms?
    if (VIRTUAL_ROOMS) return onVirtualTrackRemoved(this, track);

    const type = track.getType();
    const videoType = track.getVideoType();
    const jitsiId = this.getJitsiIdFromTrack(track);
    const trackId = track.getTrackId();

    const index = this.tracks.findIndex((t) => trackId === t.getTrackId());

    const user = User.getByJitsiId(jitsiId);

    if (index === -1) {
      const tracks = this.tracks
        .map((t) => [t.getType(), t.getVideoType(), t.getTrackId(), this.getJitsiIdFromTrack(t)].join('|'))
        .join(',\n');

      const hasMatchingTrack = this.tracks.some(
        (t) => type === t.getType() && videoType === t.getVideoType() && jitsiId === this.getJitsiIdFromTrack(t),
      );

      const systemErrorReport = `System Error Report: A/an ${type} track of type ${videoType} could not be removed.
        TrackId: ${trackId}
        JitsiId: ${jitsiId}.
        user.id: ${user?.id}.
        user.name: ${user?.name}.
        JitsiId: ${jitsiId}.
        Potential Match: ${hasMatchingTrack}.
        Available tracks were:
        ${tracks}`;

      log(WARN, systemErrorReport);

      getApp().ws?.send(
        '[recorderErrorReport]' +
          JSON.stringify({
            index,
            type,
            videoType,
            jitsiId,
          }),
      );

      return;
    }

    this.tracks.splice(index, 1);

    // Show or hide whiteboard/screenshare based on available tracks
    this.setDisplay();

    // If it's a video track being turned off, we need to set the user's "isOnCamera" setting to false.
    if (videoType === 'camera') {
      user.isOnCamera = false;
    }

    // Show or hide video box
    this.showVideoBox();
  }

  // Show the video box if there someone is on video
  showVideoBox() {
    //const count = User.all().filter((u) => u.visible && u.isOnCamera).length;
    const count = User.all().filter((u) => u.visible && u.isOnCamera && u.roomId === this.name).length;

    getApp().showVideo = count > 0;

    // issues with being on video and screenshare concurrently...
    // 1.) bring on video to start, then turning on screenshare: The video will turn off and show screenshare.  AFter turning OFF screenshare, the video box opens for both and the video is displayed.
    // 2.) being on screenshare to start, then turning on video: THe video will not display for local or non local.  After turning OFF screenshare, the video box opens for both and the video is displayed.
  }

  setDisplay() {
    const videoTracks = this.tracks.filter((t) => t.getType() === 'video' && !t.isMuted());
    this.display = videoTracks.some((t) => t.getVideoType() === 'desktop') ? 'screenshare' : 'whiteboard';
    // getApp().showVideo = videoTracks.some((t) => t.getVideoType() !== 'desktop')
  }

  onTrackAudioLevelChanged(id: string, volume: number) {
    const user = User.getByJitsiId(id);

    if (!user) return;

    user.audioLevel = volume;
  }

  onConferenceJoined() {
    // see fixme in leave()
    // when someone is on mic and changes rooms, it somehow calls this twice
    if (this.joined && !this.joining) return;

    this.joined = true;
    this.joining = false;
    this.rejoining = false;                    // recovery finished
    this.connection.overlayMessage = '';       // clear Reconnecting…/error    

    // Make sure that the user off mic., off video, and off screensharing upon joining a room.  This is useful for when changing between breakout rooms and the main room.
    const app = getApp();
    app.isScreenShareEnabled = false;
    app.isVideoEnabled = false;
    app.isAudioEnabled = false;

    // Are we using the virtual rooms mode of breakout rooms?
    // In virtual mode the main conference serves every room: use the room the user is in.
    const room = VIRTUAL_ROOMS
      ? (((Room as any).get(this.connection.currentRoomName ?? this.name) as Room) ?? this)
      : this;

    return this.refreshLocalTracks().finally(() => {
      const index = room.messages.length;
      app.ws?.send(`[updateMe]${index}`);
      if (app.currentChat !== app.currentPrivateChat) {
        app.currentChat = room.name;
      }
      console.log('[~conference] joined', this.name);
      this.resolveJoin();
    });
  }

  onDataChannelOpened() {
    if (VIRTUAL_ROOMS) resubscribe(this.connection);
  }

  async refreshLocalTracks() {
    const { isScreenShareEnabled, isAudioEnabled, isVideoEnabled } = getApp();

    const tracks = [] as ('audio' | 'camera' | 'desktop')[];
    if (isAudioEnabled) {
      tracks.push('audio');
    }
    if (isVideoEnabled) {
      tracks.push('camera');
    }
    if (isScreenShareEnabled) {
      tracks.push('desktop');
    }

    return getLocalTrack(tracks).then((tracks) => this.onLocalTracks(tracks));
  }

  attemptRejoin(attempt = 1) {
      const MAX_ATTEMPTS = 3;
      const RETRY_MS = 5000;
      if (this.joined) return;
      if (attempt > MAX_ATTEMPTS) {
          this.rejoining = false;
          this.connection.overlayMessage = 'An error occured while joining the conference. Please refresh!';
          return;
      }
      this.rejoining = true;
      console.log(`[~conference] rejoin attempt ${attempt}`);

      // Wait for leave() to fully dispose before re-joining, or we hit "conference with same name already created".
      Promise.resolve()
          .then(() => {
              try { return this.conference.leave(); } catch (e) { return null; }
          })
          .then(() => {
              if (this.joined) return;
              this.join();
          })
          .catch((e) => console.log('[~conference] rejoin threw', e));

      setTimeout(() => {
          if (!this.joined) this.attemptRejoin(attempt + 1);
      }, RETRY_MS);
  }

// onConferenceFailed() {
//    console.log('[~conference] joining conference failed');
//    this.connection.overlayMessage = 'An error occured while joining the conference. Please refresh!';
//    this.joined = false;
//    this.joining = false;
//    this.rejectJoin(new Error('Couldnt join conference'));
//  }
  onConferenceFailed(reason, ...args) {
    console.log('[~conference] CONFERENCE_FAILED reason:', reason, args);

    // Transient: Jicofo (the focus) restarted — recoverable.
    if (reason === 'conference.focusLeft') {
        const app = getApp();

        // If a recording is active, a silent rejoin re-creates the conference
        // and orphans the recording (app.recordingSession goes stale;
        // onRecorderStateChanged does NOT re-fire on rejoin). For the recording
        // case, fall back to the overlay instead of auto-rejoining.
        if (app.recorderState === 'on') {
            console.log('[~conference] focusLeft during active recording — overlay, no auto-rejoin');
            this.connection.overlayMessage = 'An error occured while joining the conference. Please refresh!';
            this.joined = false;
            this.joining = false;
            this.rejectJoin(new Error('Couldnt join conference (recording active)'));
            return;
        }

        // No recording: safe to silently recover.
        console.log('[~conference] focusLeft — attempting reconnect');
        this.connection.overlayMessage = 'Reconnecting…';
        this.joined = false;
        this.joining = true;            // we ARE actively rejoining
        this.attemptRejoin();
        return;
    }

    // Fatal reasons: existing behavior.
    console.log('[~conference] fatal conference failure');
    this.connection.overlayMessage = 'An error occured while joining the conference. Please refresh!';
    this.joined = false;
    this.joining = false;
    this.rejectJoin(new Error('Couldnt join conference'));
  }

  onConferenceLeft() {
    console.log('[~conference] local user left');
    this.joined = false;
    this.joining = false;
    getApp().showVideo = false; // To prevent the video box remaining open when moving among breakout rooms.
  }

  onUserJoined(id: string, user: any) {
    console.log('[~conference] user joined', id, user);
  }

  onUserLeft(id: string, user: any) {
    console.log('[~conference] user left', id, user);
    // User.get(id)?.delete()
  }

  onDominantSpeakerChanged(id: string, previous?: string[]) {
    this.dominantSpeaker = id;
    setSpeakerOrder(id, previous);
  }

/*
  onDominantSpeakerChanged(id: string) {
    console.log('[~conference] dominant speaker changed', id);
    this.dominantSpeaker = id;
  }
*/

  sendMessage(msg: string, to = '') {
    getApp().ws?.send(
      '[chat]' +
        JSON.stringify({
          to,
          msg,
        }),
    );
  }

  async leave() {
    console.log('[~conference] leaving', this.name);

    const tracks = this.tracks.filter((t) => t.isLocal());

    // FIXME: this leads to onConferenceJoined being called for no reason. Maybe we should just renmove it?
    await Promise.all(tracks.filter((t): t is JitsiLocalTrack => t.isLocal()).map((track) => this.conference.removeTrack(track)));
    return this.conference.leave().then(() => {
      // this.unbindEvents()
    });
  }

  async join() {
    console.log('[~conference] joining', this.name);
    this.joining = true;
    // if (!this.conference) {
    this.init();
    // }
    return this.conference.join();
  }

  getNonLocalVideo() {
    return this.tracks.find((track) => !track.isLocal() && track.getType() === 'video');
  }

  getLocalVideo() {
    return this.tracks.find((track) => track.isLocal() && track.getType() === 'video') as JitsiLocalTrack | undefined;
  }

  getLocalAudio() {
    return this.tracks.find((track) => track.isLocal() && track.getType() === 'audio') as JitsiLocalTrack | undefined;
  }

  getLocalTracks() {
    return this.tracks.filter((track) => track.isLocal()) as JitsiLocalTrack[];
  }

  getDominantVideo() {
    return this.tracks.find((track) => {
      return (
        !track.isLocal() &&
        track.getType() === 'video' &&
        this.conference &&
        // Are we using the virtual rooms mode of breakout rooms?
        track.getParticipantId() === (VIRTUAL_ROOMS ? dominantSpeakerIn(this.name) : this.conference.lastDominantSpeaker)
        //track.getParticipantId() === this.conference.lastDominantSpeaker
      );
    }) as JitsiRemoteTrack | undefined;
  }

  getScreenShareVideo() {
    return this.tracks.find((track) => {
      return !track.isLocal() && track.getType() === 'video' && track.getVideoType() === 'desktop';
    });
  }

  getLocalUserId() {
    return this.connection.id;
  }

  getLocalUser() {
    return User.get(this.connection.id) as User;
  }

  getUserEmoji(id: string) {
    return User.get(id)?.emoji;
  }

  getUserFeedback(id: string) {
    return User.get(id)?.feedback;
  }

  getUserStatus(id: string) {
    return User.get(id)?.userStatus;
  }

  getUserHand(id: string) {
    return User.get(id)?.hand ? '✋' : '';
  }

  getUserName(id: string) {
    return User.get(id)?.name ?? 'Unknown User';
  }
}
